import { notEmpty } from "@product/scmp-sdk";
import { useMountEffect } from "@react-hookz/web";
import first from "lodash/first";
import type { FunctionComponent } from "react";
import { useState } from "react";
import type { PreloadedQuery } from "react-relay";
import { graphql, useFragment, usePreloadedQuery } from "react-relay";

import { query } from "~/components/content/content-schema-render/div/photo-gallery";
import { sendGATracking } from "~/components/tracking/google-analytics/apis";
import type { photoGalleryQuery } from "~/queries/__generated__/photoGalleryQuery.graphql";
import type { photoGalleryWidgetContent$key } from "~/queries/__generated__/photoGalleryWidgetContent.graphql";
import type { photoGalleryWidgetGallery$key } from "~/queries/__generated__/photoGalleryWidgetGallery.graphql";

import { DynamicPhotoGalleryDialog } from "./dynamics";
import {
  BottomCell,
  Image,
  ImageGrid,
  Indicator,
  IndicatorIcon,
  IndicatorValue,
  MainCell,
} from "./styles";

type ComponentProps = {
  className?: string;
  galleryEntityId: string;
  photoGalleryQueryReference: PreloadedQuery<photoGalleryQuery>;
  reference: photoGalleryWidgetContent$key;
};

export const PhotoGalleryWidget: FunctionComponent<ComponentProps> = ({
  className,
  galleryEntityId,
  photoGalleryQueryReference,
  reference: reference_,
}) => {
  const content = useFragment<photoGalleryWidgetContent$key>(
    graphql`
      fragment photoGalleryWidgetContent on Content {
        ...photoGalleryDialogContent
      }
    `,
    reference_,
  );

  const gallery_ = usePreloadedQuery(query, photoGalleryQueryReference);
  const gallery = useFragment<photoGalleryWidgetGallery$key>(
    graphql`
      fragment photoGalleryWidgetGallery on Gallery {
        images {
          ...photoGalleryDialogImage
          width
          height
          title
          url
          resized: style(filter: { style: "portrait_mq" }) {
            url
          }
        }
      }
    `,
    gallery_.content,
  );

  const filteredImages = gallery?.images?.filter(notEmpty) ?? [];

  const mainImage = first(filteredImages);
  const bottomImages = filteredImages.slice(1, 4);

  const computeArtDirection = (image: (typeof filteredImages)[number]) => {
    if (!image.height || !image.width) return "portrait";
    return image.height / image.width >= 1 ? "portrait" : "landscape";
  };

  const [openedItemIndex, setOpenedItemIndex] = useState<number>();

  const handleOpenImageGallery = (index: number) => {
    setOpenedItemIndex(index);
    sendGATracking({
      action: "Article Gallery Widget/Click",
      category: "Article Gallery Widget",
      label: window.location.href,
    });
  };

  useMountEffect(() => {
    sendGATracking({
      action: "Article Gallery Widget/Impression",
      category: "Article Gallery Widget",
      label: window.location.href,
    });
  });

  if (!mainImage) return null;

  return (
    <ImageGrid className={className}>
      <MainCell>
        <Image
          $artDirection={computeArtDirection(mainImage)}
          $aspectRatio="3 / 2"
          $objectFit="cover"
          alt={mainImage.title ?? undefined}
          src={mainImage.resized?.url ?? mainImage.url}
          onClick={() => handleOpenImageGallery(0)}
        />
      </MainCell>
      {bottomImages.length > 0 &&
        bottomImages.map((image, index) => (
          <BottomCell key={index}>
            <Image
              $artDirection={computeArtDirection(image)}
              $aspectRatio="3 / 2"
              $objectFit="cover"
              alt={image.title ?? undefined}
              src={image.resized?.url ?? image.url}
              onClick={() => handleOpenImageGallery(index + 1)}
            />
          </BottomCell>
        ))}
      {filteredImages.length > 4 && (
        <Indicator onClick={() => handleOpenImageGallery(0)}>
          <IndicatorIcon />
          <IndicatorValue>{filteredImages.length}</IndicatorValue>
        </Indicator>
      )}
      {openedItemIndex !== undefined && (
        <DynamicPhotoGalleryDialog
          galleryEntityId={galleryEntityId}
          images={filteredImages}
          initialIndex={openedItemIndex}
          reference={content}
          onClose={() => setOpenedItemIndex(undefined)}
        />
      )}
    </ImageGrid>
  );
};

PhotoGalleryWidget.displayName = "PhotoGalleryWidget";
